import React, {useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {StateParams} from "../../store/reducers";
import {IPatientDetails, IPatientAppointmentSummaryView} from "../../models";
import {useNavigate, useParams} from "react-router-dom";
import {fetchPatient, fetchPatientScreeningEligibility, fetchPracticeProviders, clearEligibilityResult} from "../../store/actions/screenerQuestions.action";
import {DateTime} from "luxon";
import {PatientScreenerStatusPill} from "../generic/PatientScreenerStatusPill";
import {ArrowLeftIcon} from "@heroicons/react/20/solid";
import {InformationCircleIcon, ExclamationCircleIcon} from "@heroicons/react/24/outline";
import { clearUserScreenerId } from "../../store/actions/account.action";

const PatientScreen = () => {
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const params = useParams();
    const {patient, eligibilityResult, practiceProviders} = useSelector((state: StateParams) => state.screener)
    const {provider, loginSource} = useSelector((state: StateParams) => state.account)

    const [todayAppointment, setTodayAppointment] = useState<IPatientAppointmentSummaryView | undefined>(undefined)
    const [selectedProviderId, setSelectedProviderId] = useState<string>("")
    const [selectedProviderEnabled, setSelectedProviderEnabled] = useState<boolean | undefined>(undefined)
    const [isProviderLockedByAppointment, setIsProviderLockedByAppointment] = useState<boolean>(false)

    // Reset all state when navigating to a different patient
    useEffect(() => {
        if (params.id) {
            dispatch(fetchPatient(params.id))
            dispatch(clearUserScreenerId())
            dispatch(clearEligibilityResult())
            setTodayAppointment(undefined)
            setSelectedProviderId("")
            setSelectedProviderEnabled(undefined)
            setIsProviderLockedByAppointment(false)
        }
    }, [params.id]);

    // Step 1: patient loaded — find today's appointment and call eligibility
    useEffect(() => {
        if (!patient) return
        if (patient.appointments === undefined) return  // v1 path — no change

        const todayLocal = DateTime.local()
        const apptToday = patient.appointments.find(appt => {
            // Compare in local device timezone — same reference the appointments list uses.
            // UTC comparison incorrectly shifts the day boundary (e.g. 9pm EST = Aug 6 local, Aug 7 UTC).
            const d = DateTime.fromJSDate(new Date(appt.appointmentDate))
            return d.hasSame(todayLocal, 'day')
        })
        setTodayAppointment(apptToday)

        // Always call eligibility first — with appointmentId if one matches today, without otherwise
        dispatch(fetchPatientScreeningEligibility({
            patientId: patient.id,
            ...(apptToday && { appointmentId: apptToday.id })
        }))
    }, [patient?.id])

    // Step 2: eligibility returned — if eligible, fetch providers
    useEffect(() => {
        if (!eligibilityResult || !patient) return
        if (patient.appointments === undefined) return  // v1 path — skip

        if (eligibilityResult.isEligibleForScreeningBasedOnStatus && eligibilityResult.hasPendingOrElapsedScreeners) {
            dispatch(fetchPracticeProviders())
        }
    }, [eligibilityResult])

    // Step 3: providers loaded — auto-handle single-provider case or lock to appointment's provider
    useEffect(() => {
        if (!practiceProviders) return

        const appointmentProviderId = todayAppointment?.provider?.id
        const appointmentMatch = appointmentProviderId
            ? practiceProviders.find(p => p.id === appointmentProviderId)
            : undefined

        if (appointmentMatch) {
            // Lock to the appointment's provider — no manual selection needed
            setSelectedProviderId(appointmentMatch.id)
            setSelectedProviderEnabled(appointmentMatch.isScreeningEnabled)
            setIsProviderLockedByAppointment(true)
        } else if (practiceProviders.length === 1) {
            setSelectedProviderId(practiceProviders[0].id)
            setSelectedProviderEnabled(practiceProviders[0].isScreeningEnabled)
        } else if (practiceProviders.length === 0) {
            setSelectedProviderEnabled(true)
        }
        // ≥2 providers, no appointment match: show dropdown, wait for user selection
    }, [practiceProviders])

    const handleProviderSelect = (providerId: string) => {
        setSelectedProviderId(providerId)
        const found = practiceProviders?.find(p => p.id === providerId)
        setSelectedProviderEnabled(found?.isScreeningEnabled)
    }

    const renderPatientDetails = (patient: IPatientDetails) => {
        const isV2Path = patient.appointments !== undefined
        const patientIsEligible = eligibilityResult?.isEligibleForScreeningBasedOnStatus === true &&
            eligibilityResult?.hasPendingOrElapsedScreeners === true

        // Show dropdown once eligible and practice has multiple providers
        const showProviderDropdown = isV2Path && patientIsEligible &&
            practiceProviders !== undefined && practiceProviders.length >= 2

        // Blocked message: provider not enabled takes priority over eligibility reason
        const screeningBlockedMessage: string | null =
            selectedProviderEnabled === false
                ? "This provider is not enabled for screening."
                : (isV2Path ? (eligibilityResult?.screeningIneligibilityReason ?? null) : null)

        return <div className="flex flex-col mt-3 p-2">
            <div className="grid grid-cols-4 gap-4">
                <div className="flex flex-col mt-1">
                    <span className={"text-md font-semibold text-gray-700"}> First Name </span>
                    <span className={"text-md "}>{patient.firstName}</span>
                </div>
                <div className="flex flex-col mt-1">
                    <span className={"text-md font-semibold text-gray-700"}> Last Name </span>
                    <span className={"text-md "}>{patient.lastName}</span>
                </div>
                <div className="flex flex-col mt-1">
                    <span className={"text-md font-semibold text-gray-700"}> Gender </span>
                    <span className={"text-md "}>{patient.gender}</span>
                </div>
                <div className="flex flex-col mt-1">
                    <span className={"text-md font-semibold text-gray-700"}> DOB </span>
                    <span className={"text-md "}>{formatDob(patient.dob)}</span>
                </div>
                <div className="flex flex-col mt-1">
                    <span className={"text-md font-semibold text-gray-700"}> Phone Number </span>
                    <span className={"text-md "}>{patient.contactPhoneNumber}</span>
                </div>
                <div className="flex flex-col mt-1">
                    <span className={"text-md font-semibold text-gray-700"}> EMR Number </span>
                    <span className={"text-md "}>{patient.emrId ? patient.emrId : "N/A"}</span>
                </div>
                <div className="flex flex-col mt-1">
                    <span className={"text-md font-semibold text-gray-700"}> Zip code </span>
                    <span className={"text-md "}>{patient.zipCode}</span>
                </div>
            </div>

            <div className="flex flex-row flex-wrap items-center gap-2 mt-5">
                {showProviderDropdown && (
                    <select
                        className="border border-gray-300 rounded-md px-3 py-2 text-sm bg-white text-black disabled:opacity-60 disabled:cursor-not-allowed"
                        value={selectedProviderId}
                        onChange={(e) => handleProviderSelect(e.target.value)}
                        disabled={isProviderLockedByAppointment}
                    >
                        <option value="">Select a physician</option>
                        {practiceProviders!.map(p => (
                            <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                    </select>
                )}
                <PatientScreenerStatusPill value={{
                    hasPendingOrElapsedScreeners: patient.hasPendingOrElapsedScreeners,
                    isEligibleForScreeningBasedOnStatus: patient.isEligibleForScreeningBasedOnStatus,
                    userId: patient.id,
                    dob: patient.dob,
                    eligibilityResult,
                    isV2Path,
                    selectedProviderId,
                    todayAppointmentId: todayAppointment?.id,
                    selectedProviderEnabled,
                    showProviderDropdown,
                }}/>
                <button
                    className="items-center px-2 py-2 border text-sm font-medium rounded-md bg-orange border-white text-white"
                    onClick={() => {
                        localStorage.removeItem("sj:patient-session-id");
                        Object.keys(sessionStorage)
                            .filter(k => k.startsWith('pf_'))
                            .forEach(k => sessionStorage.removeItem(k));
                        navigate(`/patient-forms/${patient.id}/create`, { state: { returnTo: `/patients/${patient.id}` } });
                    }}
                >
                    Patient Forms
                </button>
            </div>

            {screeningBlockedMessage && (
                <div className="mt-2 mb-2 p-2 bg-[#FEF2F2] border border-[#EF4444] rounded-lg">
                    <div className="flex items-center">
                        <ExclamationCircleIcon className="size-8 text-[#EF4444]" aria-hidden="true"/>
                        <div className="ml-2">
                            <p className="text-sm text-gray-700">{screeningBlockedMessage}</p>
                        </div>
                    </div>
                </div>
            )}
            {isProviderLockedByAppointment && (
                <div className="mt-2 mb-2 p-2 bg-[#EFF6FF] border border-[#3B82F6] rounded-lg">
                    <div className="flex items-center">
                        <InformationCircleIcon className="size-8 text-[#3B82F6]" aria-hidden="true"/>
                        <div className="ml-2">
                            <p className="text-sm text-gray-700">Provider selected as part of active appointment today.</p>
                        </div>
                    </div>
                </div>
            )}

            <div className="flex flex-row flex-wrap max-h-[500px] min-w-[100%] overflow-y-auto">
                {patient.screenerHistory.map(screenerHistoryItem => {
                    return <div className={"flex flex-col mt-5 min-w-[100%]"} key={screenerHistoryItem.visit}>
                        <hr/>
                        <div className="flex flex-col mt-2">
                            <span className={"text-md font-semibold text-gray-700"}> Visit Date </span>
                            <span className={"text-md "}>{formatDateTime(screenerHistoryItem.createdAt)}</span>
                        </div>
                        <div className="flex flex-col mt-2">
                            <span className={"text-md font-semibold text-gray-700"}> Status </span>
                            <span className={"text-md "}>{screenerHistoryItem.isComplete ? "Complete" : "Incomplete"}</span>
                        </div>
                        <div className="flex flex-col mt-2">
                            <span className={"text-md font-semibold text-gray-700"}> Screeners </span>
                            {screenerHistoryItem.screeners.map(screener => {
                                const isComplete = screener.isComplete ? "Complete" : "Incomplete"
                                return <div className={"flex"} key={screener.id}>
                                    <div className="flex flex-row space-x-2">
                                        <span className={"text-md text-gray-700"}> {screener.slug} </span>
                                        <span className={`text-md font-semibold ${screener.isComplete ? "text-sjGreen" : "text-red-600"}`}>{isComplete}</span>
                                    </div>
                                </div>
                            })}
                        </div>
                    </div>
                })}
            </div>
        </div>
    }

    const formatDob = (dob: Date) => DateTime.fromISO(dob.toString(), {zone: 'utc'}).toFormat('MM-dd-yyyy')
    const formatDateTime = (dob: Date) => DateTime.fromISO(dob.toString(), {zone: 'utc'}).toFormat('MM-dd-yyyy HH:mm')
    const dischargedNextScreeningDateIsInFuture = patient?.dischargedNextScreeningDate && new Date(patient.dischargedNextScreeningDate) > new Date()

    return (
        <main className="h-screen mx-auto px-1 lg:px-8 lg:pt-6 bg-sky-light-lg bg-no-repeat bg-cover">
            <ArrowLeftIcon className="h-5 w-5" aria-hidden="true" onClick={() => navigate("/patients?page=1")}/>
            <div className="flex space-x-10 pt-2">
                <h1 className="text-xl font-semibold"> Patient Details </h1>
            </div>

            {patient ? renderPatientDetails(patient) : null}
        </main>
    )
}

export default PatientScreen
